import type { IndicesItemDataType } from "./type";
import { KlineColumnClient } from "./KlineColumnClient";
import { ShellLink } from "@/src/components";
import NextImage from "@/src/components/NextImage";
import React from "react";
import { Tooltip } from "antd";
import { cn } from "utils";
import { formatBigNumber } from "@/src/utils";

/**
 * @description 指数卡片单项属性
 */
export interface IndicesItemProps extends IndicesItemDataType {
  /** 类型，用于判断是加密货币还是股票 */
  type: "stock" | "crypto";
  /** 链接地址，如果提供则整个卡片可点击跳转 */
  href?: string;
  className?: string;
}

/**
 * @description 单个指数卡片组件，只负责展示 UI，不参与数据获取逻辑
 */
export const IndicesItem: React.FC<IndicesItemProps> = ({
  type,
  index,
  symbol,
  logo,
  value,
  change,
  change_percentage,
  sparkline,
  href,
  className,
}) => {
  const isUp = Number(change_percentage) > 0;
  const isDown = Number(change_percentage) < 0;
  const isFlat = Number(change_percentage) === 0;

  const percentageText = isFlat
    ? "0%"
    : `${formatBigNumber(change_percentage, {
        needFormat: true,
        trimZeros: true,
        precision: 2,
      })}%`;

  const changePrefix = isFlat ? "" : isDown ? "-" : "+";

  const content = (
    <div
      className={cn(
        "flex flex-col justify-between rounded-3 md:bg-background_solid_neutral_primary bg-transparent md:px-4 px-0 md:py-4 py-3 w-full",
        // { "hover:bg-background_white_overlay10": !!href && isPc },
        className,
      )}
    >
      <div className="flex flex-col">
        {/* 股票 logo 和名称 */}
        <div className="flex items-center gap-2">
          {type === "crypto" && (
            <NextImage
              src={logo}
              alt="symbol-logo border border-border_solid rounded-full"
              className="rounded-full border border-border_solid md:block hidden"
              width={32}
              height={32}
            />
          )}
          <div className="flex flex-col min-w-0 flex-1">
            <Tooltip
              title={index}
              arrow={false}
              placement="bottomLeft"
              color="#252525"
              align={{
                offset: [0, 2],
              }}
              classNames={{
                root: "rounded-[4px] border border-background_white_overlay10 overflow-hidden",
                body: "!px-[6px] !py-1 body_xs !min-h-[20px]",
              }}
            >
              <span className="body_medium_l text-content_primary md:block hidden truncate whitespace-nowrap">
                {index}
              </span>
            </Tooltip>
            <span className="md:body_s body_medium_m md:text-content_secondary text-content_primary truncate">
              {symbol}
            </span>
          </div>
        </div>

        {/* K 线：使用客户端组件包装，避免 SSR 时 window is not defined 错误 */}
        <div className="md:mt-3 mt-1 rounded-2 overflow-hidden ml-[-4px]">
          {sparkline && sparkline.length > 0 ? (
            <KlineColumnClient
              data={sparkline}
              change={Number(change_percentage)}
            />
          ) : (
            <div data-testid="indices-sparkline" className="h-[29px] w-full" />
          )}
        </div>
        {/* 价格 */}
        <div className="md:body_medium_l body_medium_m text-content_primary md:mt-[2px] mt-0">
          ${formatBigNumber(value, { needFormat: true })}
        </div>
        {/* 涨跌幅 */}
        <div className="flex flex-row items-center gap-1 mt-1">
          <div
            className={cn(
              "flex items-center justify-end md:body_bold_s body_bold_xs px-[6px] py-[2px] rounded-1",
              {
                "bg-background_accent1_overlay20": isUp,
                "bg-background_accent2_overlay20": isDown,
                "bg-background_white_overlay5": isFlat,
              },
              {
                "text-content_accent1_primary": isUp,
                "text-content_accent2_primary": isDown,
                "text-content_tertiary": isFlat,
              },
            )}
          >
            {isUp ? "+" : ""}
            {percentageText}
          </div>
          {type === "stock" && (
            <div className="body_s text-content_secondary md:block hidden">
              {changePrefix}${formatBigNumber(Math.abs(change ?? 0))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <ShellLink
        aria-label="Indices Item"
        data-openreplay-label={`Indices Item Link | ${href}`}
        id="lnk-indices-item"
        href={href}
        className={cn("block", className)}
      >
        {content}
      </ShellLink>
    );
  }

  return content;
};
